/**
 * 64×64 chunks, each with a dirty rectangle: step() only visits cells inside the rectangles.
 * Rects are stored flat as [minX, minY, maxX, maxY] (inclusive, absolute cell coordinates, clipped to the chunk).
 */

export const CHUNK_SHIFT = 6;
export const CHUNK_SIZE = 1 << CHUNK_SHIFT;

const EMPTY_MIN = 0x7fffffff;
const EMPTY_MAX = -1;

export interface ChunkGrid {
  readonly cols: number;
  readonly rows: number;
  /** Rects visited during the current step (may grow while stepping). */
  cur: Int32Array;
  /** Rects for the next step, accumulated from changes. */
  next: Int32Array;
}

function clearRects(rects: Int32Array): void {
  for (let i = 0; i < rects.length; i += 4) {
    rects[i] = EMPTY_MIN;
    rects[i + 1] = EMPTY_MIN;
    rects[i + 2] = EMPTY_MAX;
    rects[i + 3] = EMPTY_MAX;
  }
}

export function createChunkGrid(width: number, height: number): ChunkGrid {
  const cols = (width + CHUNK_SIZE - 1) >> CHUNK_SHIFT;
  const rows = (height + CHUNK_SIZE - 1) >> CHUNK_SHIFT;
  const grid: ChunkGrid = { cols, rows, cur: new Int32Array(cols * rows * 4), next: new Int32Array(cols * rows * 4) };
  clearRects(grid.cur);
  clearRects(grid.next);
  return grid;
}

/** Starts a step: the accumulated rects become current, and a fresh set starts accumulating. */
export function beginChunkStep(grid: ChunkGrid): void {
  const t = grid.cur;
  grid.cur = grid.next;
  grid.next = t;
  clearRects(grid.next);
}

function grow(rects: Int32Array, r: number, x0: number, y0: number, x1: number, y1: number): void {
  if (x0 < rects[r]) rects[r] = x0;
  if (y0 < rects[r + 1]) rects[r + 1] = y0;
  if (x1 > rects[r + 2]) rects[r + 2] = x1;
  if (y1 > rects[r + 3]) rects[r + 3] = y1;
}

/** Marks cells [x0..x1] × [y0..y1] (inclusive, clipped to the map) as needing simulation, now and next step. */
export function wakeRegion(grid: ChunkGrid, width: number, height: number, x0: number, y0: number, x1: number, y1: number): void {
  if (x0 < 0) x0 = 0;
  if (y0 < 0) y0 = 0;
  if (x1 >= width) x1 = width - 1;
  if (y1 >= height) y1 = height - 1;
  if (x0 > x1 || y0 > y1) return;
  const cx0 = x0 >> CHUNK_SHIFT;
  const cx1 = x1 >> CHUNK_SHIFT;
  const cy0 = y0 >> CHUNK_SHIFT;
  const cy1 = y1 >> CHUNK_SHIFT;
  for (let cy = cy0; cy <= cy1; cy++) {
    const top = cy << CHUNK_SHIFT;
    const ry0 = y0 > top ? y0 : top;
    const ry1 = y1 < top + CHUNK_SIZE - 1 ? y1 : top + CHUNK_SIZE - 1;
    for (let cx = cx0; cx <= cx1; cx++) {
      const left = cx << CHUNK_SHIFT;
      const rx0 = x0 > left ? x0 : left;
      const rx1 = x1 < left + CHUNK_SIZE - 1 ? x1 : left + CHUNK_SIZE - 1;
      const r = (cy * grid.cols + cx) * 4;
      grow(grid.cur, r, rx0, ry0, rx1, ry1);
      grow(grid.next, r, rx0, ry0, rx1, ry1);
    }
  }
}

export function isChunkAwake(rects: Int32Array, chunk: number): boolean {
  return rects[chunk * 4 + 2] >= rects[chunk * 4];
}

/** Number of chunks with a non-empty dirty rect for the next step. */
export function countAwakeChunks(grid: ChunkGrid): number {
  let n = 0;
  for (let c = 0; c < grid.cols * grid.rows; c++) if (isChunkAwake(grid.next, c)) n++;
  return n;
}
